import { alpha, lighten, darken } from "@mui/material/styles";

export const DEFAULT_CUSTOM_OPTION_COLOR = "#64748b";

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const TASK_STATUS_COMPLETED_COLOR = "#16a34a";

export const TASK_STATUS_PENDING_COLOR = "#f59e0b";

export const TASK_STATUS_IN_PROGRESS_COLOR = "#3b82f6";

export const TASK_STATUS_CANCELLED_COLOR = "#dc2626";

export const DEFAULT_TYPE_OPTIONS = [];
export const DEFAULT_PRIORITY_OPTIONS = [];

/** Built-in statuses always shown first in forms and list dropdowns. */
export const DEFAULT_STATUS_OPTIONS = [
  { value: "Pending", color: TASK_STATUS_PENDING_COLOR },
  { value: "In Progress", color: TASK_STATUS_IN_PROGRESS_COLOR },
  { value: "Completed", color: TASK_STATUS_COMPLETED_COLOR },
  { value: "Cancelled", color: TASK_STATUS_CANCELLED_COLOR },
];

const LEGACY_OVERDUE_STATUS_KEYS = new Set(["over due", "overdue"]);

const BUILT_IN_STATUS_KEYS = new Set([
  "pending",
  "in progress",
  "complete",
  "completed",
  "cancelled",
  "canceled",
]);

const normalizeStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

export const isBuiltInStatusValue = (value) =>
  BUILT_IN_STATUS_KEYS.has(normalizeStatusKey(value));

export const isPendingStatusValue = (value) =>
  normalizeStatusKey(value) === "pending" || isLegacyOverdueStatusValue(value);

/** Soft pill text/bg like Modified On and Bitrix overdue badges (light + dark). */
export const getStatusAccentTextColor = (theme, accentColor) =>
  theme.palette.mode === "dark" ? theme.palette.error.light : accentColor;

export const getStatusAccentSoftPillSx = (theme, accentColor, { active = false } = {}) => {
  const color = getStatusAccentTextColor(theme, accentColor);
  if (theme.palette.mode === "dark") {
    const main = theme.palette.error.main;
    return {
      color,
      bgcolor: alpha(main, active ? 0.32 : 0.22),
      border: `1px solid ${alpha(main, active ? 0.45 : 0.35)}`,
    };
  }
  return {
    color,
    bgcolor: alpha(accentColor, active ? 0.16 : 0.1),
    border: `1px solid ${alpha(accentColor, active ? 0.28 : 0.18)}`,
  };
};


export const isLegacyOverdueStatusValue = (value) =>
  LEGACY_OVERDUE_STATUS_KEYS.has(normalizeStatusKey(value));

/** Custom-only values (excludes built-in defaults and legacy Over Due). */
export const stripBuiltInStatusOptions = (options) =>
  normalizeFieldOptions(options, "status").filter(
    (item) => !isBuiltInStatusValue(item.value) && !isLegacyOverdueStatusValue(item.value)
  );

/** Built-in statuses first (with DB color overrides), then custom values. */
export const mergeStatusOptionsWithDefaults = (customOptions) => {
  const stored = normalizeFieldOptions(customOptions, "status").filter(
    (item) => !isLegacyOverdueStatusValue(item.value)
  );
  const defaults = normalizeFieldOptions(DEFAULT_STATUS_OPTIONS, "status");
  const usedKeys = new Set();

  const merged = defaults.map((def) => {
    const key = normalizeStatusKey(def.value);
    const override = stored.find((s) => normalizeStatusKey(s.value) === key);
    usedKeys.add(key);
    return override ? { value: override.value, color: def.color } : def;
  });

  stored.forEach((item) => {
    const key = normalizeStatusKey(item.value);
    if (usedKeys.has(key)) return;
    merged.push(item);
    usedKeys.add(key);
  });

  return merged;
};

export const normalizeHexColor = (value, fallback = DEFAULT_CUSTOM_OPTION_COLOR) => {
  const color = String(value || "").trim();
  return HEX_COLOR_REGEX.test(color) ? color : fallback;
};

export const getFieldOptionValue = (option) =>
  typeof option === "string"
    ? String(option || "").trim()
    : String(option?.value || option?.label || "").trim();

const getDefaultOptionsForKind = (kind) => {
  if (kind === "status") return DEFAULT_STATUS_OPTIONS;
  if (kind === "priority") return DEFAULT_PRIORITY_OPTIONS;
  return DEFAULT_TYPE_OPTIONS;
};

const getDefaultColorForKindValue = (kind, value) => {
  const lower = String(value || "").trim().toLowerCase();
  if (!lower) return DEFAULT_CUSTOM_OPTION_COLOR;
  const match = getDefaultOptionsForKind(kind).find(
    (item) => String(item.value || "").trim().toLowerCase() === lower
  );
  return match?.color || DEFAULT_CUSTOM_OPTION_COLOR;
};

export const cloneFieldOptions = (items) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    value: getFieldOptionValue(item),
    color: normalizeHexColor(item?.color, DEFAULT_CUSTOM_OPTION_COLOR),
  }));

export const normalizeFieldOptions = (items, kind = "type") => {
  const seen = new Set();
  const normalized = [];

  (Array.isArray(items) ? items : []).forEach((raw) => {
    const value = getFieldOptionValue(raw);
    if (!value) return;
    if (kind === "status" && isLegacyOverdueStatusValue(value)) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const fallbackColor = getDefaultColorForKindValue(kind, value);
    const color =
      typeof raw === "string"
        ? fallbackColor
        : normalizeHexColor(raw?.color, fallbackColor);
    normalized.push({ value, color });
  });

  return normalized;
};

export const ensureFieldOptionPresent = (
  options,
  value,
  kind = "type",
  fallbackColor = DEFAULT_CUSTOM_OPTION_COLOR
) => {
  const normalized = normalizeFieldOptions(options, kind);
  const nextValue = String(value || "").trim();
  if (!nextValue) return normalized;
  const exists = normalized.some((item) => item.value.toLowerCase() === nextValue.toLowerCase());
  if (exists) return normalized;
  return [
    {
      value: nextValue,
      color: getDefaultColorForKindValue(kind, nextValue) || fallbackColor,
    },
    ...normalized,
  ];
};

export const resolveFieldOptionColor = (
  value,
  options,
  kind = "type",
  fallbackColor = DEFAULT_CUSTOM_OPTION_COLOR
) => {
  const normalized = normalizeFieldOptions(options, kind);
  const lower = String(value || "").trim().toLowerCase();
  if (!lower) return fallbackColor;
  const match = normalized.find((item) => item.value.toLowerCase() === lower);
  return match?.color || getDefaultColorForKindValue(kind, value) || fallbackColor;
};

/** Generic soft pill (tinted bg + accent-colored text + subtle border) for status/type/priority chips. */
export const getFieldOptionSoftPillSx = (theme, accentColor, { active = false } = {}) => {
  const dark = theme.palette.mode === "dark";
  return {
    color: dark ? lighten(accentColor, 0.4) : darken(accentColor, 0.08),
    bgcolor: alpha(accentColor, dark ? (active ? 0.34 : 0.24) : active ? 0.2 : 0.14),
    border: `1px solid ${alpha(accentColor, dark ? (active ? 0.5 : 0.4) : active ? 0.36 : 0.26)}`,
  };
};

export const getFieldOptionChipSx =
  (value, options, kind = "type", fallbackColor = DEFAULT_CUSTOM_OPTION_COLOR) =>
  (theme) => {
    const color = resolveFieldOptionColor(value, options, kind, fallbackColor);
    return {
      ...getFieldOptionSoftPillSx(theme, color),
      fontWeight: 600,
      height: "auto",
      borderRadius: "999px",
      "& .MuiChip-label": {
        px: 1.1,
        py: 0.35,
        color: "inherit",
      },
    };
  };

/** Borderless inline selects for task / template table Type, Priority, Status cells. */
export const taskTableInlineSelectSx = {
  width: "max-content",
  maxWidth: "100%",
  minWidth: "unset",
  fontSize: "0.85rem",
  "&:before, &:after": { display: "none" },
  "& .MuiSelect-select": {
    py: 0.35,
    pl: 0,
    pr: "30px !important",
    display: "inline-flex",
    alignItems: "center",
    width: "auto",
    minWidth: 0,
    boxSizing: "content-box",
  },
  "& .MuiSelect-icon": {
    right: 0,
    width: 30,
    height: 30,
    top: "calc(50% - 15px)",
    fontSize: "1.65rem",
  },
  "& .MuiSelect-icon svg": {
    fontSize: "1.65rem",
  },
};

export const taskTableInlineSelectStatusSx = {
  ...taskTableInlineSelectSx,
};
